import { getCoreUtils } from "../kReusableCoreTS/kCoreHandler";

export interface IkNativeDialogOptions {
    OnClose?: () => void;
    ReturnFocusTo?: HTMLElement | null;
    ResolveReturnFocus?: () => HTMLElement | null;
    CloseOnBackdropClick?: boolean;
    ClassName?: string;
}

/** Native modal lifecycle without stylesheet injection; the caller owns its markup and CSS. */
export class kNativeDialog {
    public readonly Element: HTMLDialogElement;
    private Options: IkNativeDialogOptions;
    private Opener: HTMLElement | null;
    private Finished: boolean = false;
    private DownOutside: boolean = false;
    private constructor(Element: HTMLDialogElement, Options: IkNativeDialogOptions, Opener: HTMLElement | null) {
        this.Element = Element; this.Options = Options; this.Opener = Opener;
    }

    public static Open = (Title: string, ContentElement: HTMLElement, Options: IkNativeDialogOptions = {}): kNativeDialog | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            const Doc: Document = ContentElement.ownerDocument;
            const Element: HTMLDialogElement = Doc.createElement("dialog");
            Element.setAttribute("aria-label", Title); Element.className = Options.ClassName || "";
            Element.appendChild(ContentElement);
            const Opener: HTMLElement | null = Doc.activeElement as HTMLElement | null;
            const Made: kNativeDialog = new kNativeDialog(Element, Options, Opener);
            if (Made.Show() === false) { return null; }
            return Made;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kNativeDialog.Open"); return null; }
    };

    private Show = (): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (this.Wire() === false) { this.Finish(false); return false; }
            this.Element.ownerDocument.body.appendChild(this.Element); this.Element.showModal();
            return true;
        } catch (e) {
            this.Finish(false);
            getCoreUtils().KGlobalErrorHandler(e, "kNativeDialog.Show"); return false;
        }
    };

    private Wire = (): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            this.Element.addEventListener("cancel", this.OnCancel);
            this.Element.addEventListener("close", this.OnClosed);
            this.Element.addEventListener("pointerdown", this.OnDown);
            this.Element.addEventListener("click", this.OnBackdrop);
            return true;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kNativeDialog.Wire"); return false; }
    };

    /** ERROR-SUPPRESSED-JUSTIFIED: Escape only disposes the open layer, including after a fault latches. */
    private OnCancel = (Event: Event): void => {
        try { Event.preventDefault(); this.Close(); }
        catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kNativeDialog.OnCancel"); }
    };

    private Outside = (Event: MouseEvent): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (Event.target !== this.Element) { return false; }
            const Box: DOMRect = this.Element.getBoundingClientRect();
            return Event.clientX < Box.left || Event.clientX > Box.right || Event.clientY < Box.top || Event.clientY > Box.bottom;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kNativeDialog.Outside"); return false; }
    };

    private OnDown = (Event: PointerEvent): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try { this.DownOutside = this.Outside(Event); }
        catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kNativeDialog.OnDown"); }
    };

    private OnBackdrop = (Event: MouseEvent): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Began: boolean = this.DownOutside; this.DownOutside = false;
            if (this.Options.CloseOnBackdropClick !== false && Began === true && this.Outside(Event) === true) { this.Close(); }
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kNativeDialog.OnBackdrop"); }
    };

    /** ERROR-SUPPRESSED-JUSTIFIED: teardown releases the modal and caller cleanup after a fault; no new action starts. */
    public Close = (): void => {
        try {
            if (this.Finished === true) { return; }
            const Had: boolean = this.Element.contains(this.Element.ownerDocument.activeElement);
            if (this.Element.open === true) { this.Element.close(); }
            this.Finish(Had);
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kNativeDialog.Close"); }
    };

    /** ERROR-SUPPRESSED-JUSTIFIED: a browser close must release the layer even when the global gate is latched. */
    private OnClosed = (): void => {
        try { this.Finish(false); }
        catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kNativeDialog.OnClosed"); }
    };

    /** ERROR-SUPPRESSED-JUSTIFIED: idempotent disposal removes listeners/content and invokes only the existing cleanup callback. */
    private Finish = (ReturnFocus: boolean): void => {
        try {
            if (this.Finished === true) { return; }
            this.Finished = true;
            this.Element.removeEventListener("cancel", this.OnCancel);
            this.Element.removeEventListener("close", this.OnClosed);
            this.Element.removeEventListener("pointerdown", this.OnDown);
            this.Element.removeEventListener("click", this.OnBackdrop);
            this.Element.remove();
            if (ReturnFocus === true) { this.FocusBack(); }
            if (this.Options.OnClose !== undefined) { this.Options.OnClose(); }
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kNativeDialog.Finish"); }
    };

    /** ERROR-SUPPRESSED-JUSTIFIED: closing restores the focus owned by this layer, without starting a product action. */
    private FocusBack = (): void => {
        try {
            let Target: HTMLElement | null = this.Opener;
            if (this.Options.ReturnFocusTo !== undefined) { Target = this.Options.ReturnFocusTo; }
            if ((Target === null || Target.isConnected === false) && this.Options.ResolveReturnFocus !== undefined) {
                Target = this.Options.ResolveReturnFocus();
            }
            if (Target !== null && Target.isConnected === true && typeof Target.focus === "function") { Target.focus(); }
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kNativeDialog.FocusBack"); }
    };
}
